import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Home,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Info,
  Maximize2,
  Building2,
  Wallet,
  Coins,
  LineChart as LineChartIcon,
  Trees,
  PieChart,
  ArrowRightLeft,
} from "lucide-react";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency, formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";
import {
  CustoUnitarioCCStat,
  computeCustoAcumulado,
} from "../chartUtils";
import { ComparativoInsumosModal } from "../ComparativoInsumosModal";
import { BAR_HOVER_PROPS, TOOLTIP_CURSOR_BAND } from "@/utils/rechartsInteraction";

/* ─── Tooltip gráfico comparativo ────────────────────────────────────────── */
interface TipProps {
  active?: boolean;
  payload?: Array<{ payload: CustoUnitarioCCStat & { nameShort?: string } }>;
}

function CompareTooltip({ active, payload }: TipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 min-w-[220px]">
      <p className="text-xs font-semibold text-slate-700 leading-snug mb-2">{d.name}</p>
      <div className="flex flex-col gap-1 text-sm">
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Custo unitário</span>
          <span className="font-bold text-emerald-600">{formatCurrency(d.custoUnitario)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Custo total</span>
          <span className="font-semibold text-slate-800">{formatCurrency(d.totalCost, true)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Unidades</span>
          <span className="font-semibold text-slate-800">{formatNumber(d.unitCount)} casas</span>
        </div>
      </div>
    </div>
  );
}

function abbrev(s: string, max = 24) {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/* ─── Vista única obra (layout dashboard) ────────────────────────────────── */
function SingleObraDashboard({
  row,
  avgCost,
  onExpand,
  onInvestidoPorPlano,
}: {
  row: CustoUnitarioCCStat;
  avgCost: number;
  onExpand?: () => void;
  /** Abre o modal «Total investido por plano de contas» só desta obra */
  onInvestidoPorPlano?: () => void;
}) {
  const monthly = useMemo(
    () => computeCustoAcumulado(row.items, row.unitCount),
    [row.items, row.unitCount],
  );

  const trend = useMemo(() => {
    if (monthly.length < 2) return null;
    const first = monthly[0].custoUnitario;
    const last = monthly[monthly.length - 1].custoUnitario;
    if (first <= 0) return null;
    const pct = ((last - first) / first) * 100;
    return { first, last, pct };
  }, [monthly]);

  const pctVsMedia =
    avgCost > 0 ? ((row.custoUnitario - avgCost) / avgCost) * 100 : 0;
  const showBenchmark = Number.isFinite(pctVsMedia) && Math.abs(pctVsMedia) > 0.05;

  const unitLabel =
    row.unitCount === 1 ? "1 casa" : `${formatNumber(row.unitCount, 0)} casas`;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      {/* Cabeçalho */}
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 flex-shrink-0">
            <Home className="h-5 w-5" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-800">Custo Unitário por Obra</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Custo total investido ÷ número de unidades (R$/casa)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {onInvestidoPorPlano && (
            <button
              type="button"
              onClick={onInvestidoPorPlano}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-100 hover:bg-rose-100 transition-colors"
              title="Ver total investido por plano de contas desta obra"
            >
              <PieChart className="w-3.5 h-3.5" />
              Plano de contas
            </button>
          )}
          {onExpand && (
            <button
              type="button"
              onClick={onExpand}
              className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
              title="Expandir"
              aria-label="Expandir painel"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Resumo superior */}
      <div className="px-5 pb-4 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 items-center">
        <div className="rounded-xl bg-emerald-50/80 border border-emerald-100 px-4 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700/90">
            Custo unitário
          </p>
          <p className="text-2xl sm:text-3xl font-bold text-emerald-600 tabular-nums mt-1">
            {row.unitCount > 0 ? formatCurrency(row.custoUnitario) : "—"}
          </p>
          {trend && (
            <div
              className={cn(
                "mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
                trend.pct >= 0
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-amber-50 text-amber-800",
              )}
            >
              {trend.pct >= 0 ? (
                <TrendingUp className="w-3.5 h-3.5" />
              ) : (
                <TrendingDown className="w-3.5 h-3.5" />
              )}
              {trend.pct >= 0 ? "+" : ""}
              {formatNumber(trend.pct, 1)}% vs. primeiro mês do período
            </div>
          )}
        </div>

        <div
          className="flex justify-center py-2"
          aria-hidden
        >
          <div className="relative flex h-28 w-36 items-end justify-center rounded-2xl bg-gradient-to-b from-emerald-50 to-white border border-emerald-100/80">
            <Trees className="absolute bottom-8 left-5 h-10 w-10 text-emerald-300/90" strokeWidth={1.25} />
            <Home className="h-16 w-16 text-emerald-500 mb-2" strokeWidth={1.25} />
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Obra analisada
          </p>
          <p className="font-bold text-slate-800 leading-snug">{row.name}</p>
          <div className="space-y-2 pt-1">
            <div className="flex items-start gap-2">
              <Wallet className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Custo total investido</p>
                <p className="font-bold text-emerald-600 tabular-nums">
                  {formatCurrency(row.totalCost, true)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Número de unidades</p>
                <p className="font-bold text-emerald-600">{unitLabel}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fluxo do cálculo */}
      <div className="mx-5 mb-4 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3 sm:px-4">
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm">
          <div className="flex items-center gap-1.5 text-slate-600">
            <Wallet className="w-4 h-4 text-slate-500" />
            <span className="text-[10px] uppercase font-semibold text-slate-500">Custo total</span>
            <span className="font-bold text-slate-800 tabular-nums">
              {formatCurrency(row.totalCost, true)}
            </span>
          </div>
          <span className="text-emerald-600 font-bold text-lg">÷</span>
          <div className="flex items-center gap-1.5 text-slate-600">
            <Building2 className="w-4 h-4 text-slate-500" />
            <span className="text-[10px] uppercase font-semibold text-slate-500">Unidades</span>
            <span className="font-bold text-slate-800">{unitLabel}</span>
          </div>
          <span className="text-emerald-600 font-bold text-lg">=</span>
          <div className="flex items-center gap-1.5 text-slate-600">
            <Coins className="w-4 h-4 text-slate-500" />
            <span className="text-[10px] uppercase font-semibold text-slate-500">Custo unitário</span>
            <span className="font-bold text-emerald-600 tabular-nums">
              {row.unitCount > 0 ? `${formatCurrency(row.custoUnitario)} / casa` : "—"}
            </span>
          </div>
        </div>
      </div>

      {/* Comparativo + benchmark */}
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200 border-t border-slate-100">
        <div className="px-5 py-4 flex gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 flex-shrink-0">
            <LineChartIcon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Comparativo no período
            </p>
            {trend ? (
              <>
                <p
                  className={cn(
                    "text-xl font-bold tabular-nums mt-0.5",
                    trend.pct >= 0 ? "text-emerald-600" : "text-amber-600",
                  )}
                >
                  {trend.pct >= 0 ? "+" : ""}
                  {formatNumber(trend.pct, 1)}%
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  custo mensal/casa no 1º mês era{" "}
                  <span className="font-medium text-slate-700">{formatCurrency(trend.first)}</span>
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-400 mt-1">
                Há menos de dois meses com despesas no filtro para comparar evolução mensal.
              </p>
            )}
          </div>
        </div>

        <div className="px-5 py-4 flex gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 flex-shrink-0">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Referência (média do filtro)
            </p>
            {showBenchmark ? (
              <>
                <p className="text-xl font-bold text-slate-800 tabular-nums mt-0.5">
                  {formatCurrency(avgCost)}
                  <span className="text-sm font-normal text-slate-500"> / casa</span>
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  Você está{" "}
                  <span
                    className={cn(
                      "font-bold",
                      pctVsMedia > 0 ? "text-orange-600" : "text-emerald-600",
                    )}
                  >
                    {formatNumber(Math.abs(pctVsMedia), 1)}% {pctVsMedia > 0 ? "acima" : "abaixo"}
                  </span>{" "}
                  da média entre as obras selecionadas
                </p>
              </>
            ) : (
              <>
                <p className="text-xl font-bold text-slate-800 tabular-nums mt-0.5">
                  {formatCurrency(avgCost)}
                  <span className="text-sm font-normal text-slate-500"> / casa</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Com apenas uma obra no resultado, a média do filtro coincide com este custo unitário.
                  Selecione mais centros de custo para comparar com a média do conjunto.
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-start gap-2 px-5 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
        <span>
          Cálculo: custo total investido (parcelas do período no Sienge) dividido pelo número de unidades
          habitacionais informadas para o centro de custo.
        </span>
      </div>
    </div>
  );
}

/* ─── Modal espelho (expandir) ─────────────────────────────────────────── */
function SingleDashboardModal({
  open,
  onClose,
  row,
  avgCost,
}: {
  open: boolean;
  onClose: () => void;
  row: CustoUnitarioCCStat;
  avgCost: number;
}) {
  return (
    <Modal open={open} onClose={onClose} size="full" title="Custo unitário por obra">
      <div className="max-w-4xl mx-auto">
        <SingleObraDashboard row={row} avgCost={avgCost} onExpand={undefined} />
      </div>
    </Modal>
  );
}

/* ─── Props ─────────────────────────────────────────────────────────────── */
interface Props {
  data: CustoUnitarioCCStat[];
  loading: boolean;
  avgCost: number;
  /** Texto do período aplicado (exibido no modal Comparativo). */
  periodLabel?: string;
  /** Período para apropriações (orçamento realizado) no modal Comparativo. */
  startDate: string;
  endDate: string;
  /** Clique na barra do comparativo (ou no botão em obra única) abre o modal de plano de contas */
  onOpenInvestidoPorPlano?: (row: CustoUnitarioCCStat) => void;
}

const BAR_GREEN = "#10b981";
const BAR_ORANGE = "#f59e0b";

export function CustoUnitarioPorCC({
  data,
  loading,
  avgCost,
  periodLabel,
  startDate,
  endDate,
  onOpenInvestidoPorPlano,
}: Props) {
  const [expandOpen, setExpandOpen] = useState(false);
  const [comparativoOpen, setComparativoOpen] = useState(false);

  const chartRows = useMemo(
    () =>
      data.slice(0, 12).map((d) => ({
        ...d,
        nameShort: abbrev(d.name, 22),
      })),
    [data],
  );

  const leader = data[0];
  const multiSummary = useMemo(() => {
    if (data.length < 2) return null;
    const costs = data.map((d) => d.custoUnitario);
    return {
      max: Math.max(...costs),
      min: Math.min(...costs),
      maxRow: data.reduce((a, b) => (a.custoUnitario >= b.custoUnitario ? a : b)),
      minRow: data.reduce((a, b) => (a.custoUnitario <= b.custoUnitario ? a : b)),
    };
  }, [data]);

  if (loading) {
    return (
      <ChartContainer
        title="Custo Unitário por Obra"
        subtitle="Custo total investido ÷ número de unidades (R$/casa)"
        loading
        height={400}
      >
        <div />
      </ChartContainer>
    );
  }

  if (data.length === 0) {
    return (
      <ChartContainer
        title="Custo Unitário por Obra"
        subtitle="Custo total investido ÷ número de unidades (R$/casa)"
        empty
        emptyMessage="Nenhuma obra com despesas no período."
        height={200}
      >
        <div />
      </ChartContainer>
    );
  }

  /* ─── Uma obra: dashboard estilo referência ───────────────────────────── */
  if (data.length === 1) {
    return (
      <>
        <SingleObraDashboard
          row={data[0]}
          avgCost={avgCost}
          onExpand={() => setExpandOpen(true)}
          onInvestidoPorPlano={
            onOpenInvestidoPorPlano ? () => onOpenInvestidoPorPlano(data[0]) : undefined
          }
        />
        <SingleDashboardModal
          open={expandOpen}
          onClose={() => setExpandOpen(false)}
          row={data[0]}
          avgCost={avgCost}
        />
      </>
    );
  }

  /* ─── Várias obras: comparativo em barras ────────────────────────────── */
  const chartHeight = Math.max(340, Math.min(chartRows.length * 56, 520));

  return (
    <>
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 flex-shrink-0">
            <Home className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Custo Unitário por Obra</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Comparativo do custo por casa (R$/casa) entre as obras selecionadas
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setComparativoOpen(true)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-100 hover:bg-emerald-100 transition-colors flex-shrink-0"
          title="Analisar insumos e variação em R$/casa entre duas obras"
        >
          <ArrowRightLeft className="w-3.5 h-3.5" />
          Comparativo
        </button>
      </div>

      {/* KPIs rápidos */}
      <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
          <p className="text-[10px] font-semibold uppercase text-slate-500">Média no filtro</p>
          <p className="text-lg font-bold text-slate-800 tabular-nums">{formatCurrency(avgCost)}</p>
        </div>
        {multiSummary && (
          <>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase text-emerald-700">Maior (obra)</p>
              <p className="text-sm font-semibold text-slate-800 truncate">{multiSummary.maxRow.name}</p>
              <p className="text-lg font-bold text-emerald-700 tabular-nums">
                {formatCurrency(multiSummary.max)}
              </p>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase text-amber-800">Menor (obra)</p>
              <p className="text-sm font-semibold text-slate-800 truncate">{multiSummary.minRow.name}</p>
              <p className="text-lg font-bold text-amber-700 tabular-nums">
                {formatCurrency(multiSummary.min)}
              </p>
            </div>
          </>
        )}
      </div>

      <div className="px-2 sm:px-4 pb-2" style={{ height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartRows}
            margin={{ top: 8, right: 12, left: 8, bottom: 64 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="nameShort"
              tick={{ fontSize: 10, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
              interval={0}
              angle={-35}
              textAnchor="end"
              height={70}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#94a3b8" }}
              axisLine={false}
              tickLine={false}
              width={72}
              tickFormatter={(v: number) =>
                v >= 1_000_000
                  ? `${(v / 1_000_000).toFixed(1)}M`
                  : v >= 1000
                    ? `${(v / 1000).toFixed(0)}k`
                    : formatNumber(v, 0)
              }
            />
            <Tooltip content={<CompareTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
            <ReferenceLine
              y={avgCost}
              stroke="#94a3b8"
              strokeDasharray="5 5"
              label={{
                value: `Média ${formatCurrency(avgCost)}`,
                position: "insideTopRight",
                fill: "#64748b",
                fontSize: 10,
              }}
            />
            <Bar
              {...BAR_HOVER_PROPS}
              dataKey="custoUnitario"
              name="R$/casa"
              radius={[6, 6, 0, 0]}
              maxBarSize={48}
              style={{ cursor: onOpenInvestidoPorPlano ? "pointer" : undefined }}
              onClick={
                onOpenInvestidoPorPlano
                  ? (item) => {
                      const p = item.payload as CustoUnitarioCCStat & { nameShort?: string };
                      if (p?.costCenterId != null) onOpenInvestidoPorPlano(p);
                    }
                  : undefined
              }
            >
              {chartRows.map((entry) => (
                <Cell
                  key={entry.costCenterId}
                  fill={
                    entry.custoUnitario > avgCost * 1.02
                      ? BAR_ORANGE
                      : entry.custoUnitario < avgCost * 0.98
                        ? BAR_GREEN
                        : "#34d399"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 text-center text-xs sm:text-sm text-slate-600">
        {leader && (
          <>
            Maior custo unitário no período:{" "}
            <span className="font-semibold text-slate-800">{leader.name}</span> —{" "}
            <span className="font-bold text-emerald-700 tabular-nums">
              {formatCurrency(leader.custoUnitario)}
            </span>
            {" "}({formatNumber(data.length)} obras comparadas)
          </>
        )}
      </div>

      <div className="flex items-start gap-2 px-5 py-3 border-t border-slate-100 text-xs text-slate-500">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
        <span>
          Cálculo: custo total investido dividido pelo número de unidades habitacionais por centro de custo.
          A linha tracejada indica a média entre as obras do filtro atual.
          {onOpenInvestidoPorPlano && (
            <> Clique em uma <strong>barra</strong> para abrir o total investido por plano de contas da obra.</>
          )}
        </span>
      </div>
    </div>

    <ComparativoInsumosModal
      open={comparativoOpen}
      onClose={() => setComparativoOpen(false)}
      ccStats={data}
      periodLabel={periodLabel}
      startDate={startDate}
      endDate={endDate}
    />
    </>
  );
}
